import {
  Modal,
  TextInput,
  Textarea,
  Button,
  Stack,
  Group,
  Text,
  SegmentedControl,
  FileInput,
  Alert,
  TagsInput,
} from '@mantine/core'
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { secretsApi } from '@/api'
import { notifications } from '@mantine/notifications'
import { IconUpload, IconAlertCircle, IconKey } from '@tabler/icons-react'

interface CreateSecretModalProps {
  opened: boolean
  onClose: () => void
  onSuccess: () => void
}

export function CreateSecretModal({ opened, onClose, onSuccess }: CreateSecretModalProps) {
  const [name, setName] = useState('')
  const [inputType, setInputType] = useState<'text' | 'file'>('text')
  const [secretText, setSecretText] = useState('')
  const [secretFile, setSecretFile] = useState<File | null>(null)
  const [labels, setLabels] = useState<string[]>([])

  const createMutation = useMutation({
    mutationFn: async () => {
      let data = ''

      if (inputType === 'text') {
        // For text input, base64 encode the secret
        data = btoa(secretText)
      } else if (inputType === 'file' && secretFile) {
        // For file input, read the file and base64 encode it
        const arrayBuffer = await secretFile.arrayBuffer()
        const bytes = new Uint8Array(arrayBuffer)
        const binaryString = Array.from(bytes, (byte) => String.fromCharCode(byte)).join('')
        data = btoa(binaryString)
      }

      // Convert labels array to object
      const labelsObj: Record<string, string> = {}
      labels.forEach((label) => {
        const [key, value] = label.split('=')
        if (key && value) {
          labelsObj[key.trim()] = value.trim()
        }
      })

      const { data: response } = await secretsApi.create({
        Name: name,
        Data: data,
        Labels: Object.keys(labelsObj).length > 0 ? labelsObj : undefined,
      })
      return response
    },
    onSuccess: () => {
      notifications.show({
        title: 'Secret created',
        message: `Secret ${name} has been created successfully`,
        color: 'green',
      })
      onSuccess()
      resetForm()
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to create secret',
        color: 'red',
      })
    },
  })

  const resetForm = () => {
    setName('')
    setSecretText('')
    setSecretFile(null)
    setLabels([])
    setInputType('text')
  }

  const handleSubmit = () => {
    // Validation
    if (!name) {
      notifications.show({
        title: 'Validation error',
        message: 'Please provide a secret name',
        color: 'red',
      })
      return
    }

    if (inputType === 'text' && !secretText) {
      notifications.show({
        title: 'Validation error',
        message: 'Please provide secret data',
        color: 'red',
      })
      return
    }

    if (inputType === 'file' && !secretFile) {
      notifications.show({
        title: 'Validation error',
        message: 'Please select a file',
        color: 'red',
      })
      return
    }

    // Check name format
    const nameRegex = /^[a-zA-Z0-9][a-zA-Z0-9_.-]*$/
    if (!nameRegex.test(name)) {
      notifications.show({
        title: 'Validation error',
        message: 'Secret name must start with alphanumeric and contain only alphanumeric, underscore, hyphen, or dot',
        color: 'red',
      })
      return
    }

    if (name.length > 64) {
      notifications.show({
        title: 'Validation error',
        message: 'Secret name must be 64 characters or less',
        color: 'red',
      })
      return
    }

    createMutation.mutate()
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Create Secret" size="lg">
      <Stack>
        <Alert icon={<IconAlertCircle size={16} />} color="yellow">
          Secrets are encrypted at rest and never displayed in the UI. Store sensitive data like
          passwords, API keys, and certificates.
        </Alert>

        <TextInput
          label="Secret Name"
          placeholder="my-secret"
          description="Alphanumeric, underscore, hyphen, or dot (max 64 characters)"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          required
          leftSection={<IconKey size={16} />}
        />

        <div>
          <Text size="sm" fw={500} mb="xs">
            Input Type
          </Text>
          <SegmentedControl
            value={inputType}
            onChange={(value) => setInputType(value as 'text' | 'file')}
            data={[
              { label: 'Text', value: 'text' },
              { label: 'File', value: 'file' },
            ]}
            fullWidth
          />
        </div>

        {inputType === 'text' ? (
          <Textarea
            label="Secret Data"
            placeholder="Enter secret data..."
            description="The data will be encrypted before storage"
            value={secretText}
            onChange={(e) => setSecretText(e.currentTarget.value)}
            required
            minRows={4}
            maxRows={8}
          />
        ) : (
          <FileInput
            label="Secret File"
            placeholder="Choose a file"
            description="Upload a file to use as secret data (max 500KB)"
            value={secretFile}
            onChange={setSecretFile}
            required
            leftSection={<IconUpload size={16} />}
            accept="*/*"
          />
        )}

        <TagsInput
          label="Labels"
          placeholder="Enter labels as key=value"
          description="Press Enter after each label"
          value={labels}
          onChange={setLabels}
          splitChars={[',', ' ', ';']}
          clearable
        />

        {secretFile && (
          <Alert color="blue">
            Selected file: <strong>{secretFile.name}</strong> ({(secretFile.size / 1024).toFixed(2)} KB)
          </Alert>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={createMutation.isPending}>
            Create Secret
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
